import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Bell, Trash2, CheckCheck, Info, AlertCircle, CheckCircle, Star, Eye, Crown, Briefcase } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { fetchWithAuth } from '../utils/api';
import { toast } from '../utils/toast';
import { devLog, devError } from '../utils/logger';
import { useModalFocus } from '../utils/hooks/useModalFocus';


interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  destination: string | null;
  data: any;
  isRead: boolean;
  createdAt: string;
}

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string;
  onNavigate: (
    view: string,
    options?: {
      userId?: string;
      recipientId?: string;
      savedSearchId?: string;
      gigId?: string;
      gigTitle?: string;
    }
  ) => void;
  onNotificationRead: () => void; // Callback to refresh unread count
  onProfileRefresh?: () => void; // Callback to refresh user profile when verification status changes
  anchorRect?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
    width: number;
    height: number;
  } | null;
}

export function NotificationCenter({ 
  isOpen, 
  onClose, 
  accessToken, 
  onNavigate,
  onNotificationRead,
  onProfileRefresh,
  anchorRect,
}: NotificationCenterProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const isIosPwa =
    typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
  const isDesktopDropdown = isDesktop && !isIosPwa;
  // Kun desktop: bruk modal focus (skjul chrome). iOS PWA og Android: IKKE modal focus – header og bunn-nav skal ligge synlige bak, inaktive (talentfunn-notification-open).
  useModalFocus(isOpen && isDesktop);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(min-width: 1280px)');
    const syncDesktop = () => setIsDesktop(mediaQuery.matches);
    syncDesktop();
    mediaQuery.addEventListener('change', syncDesktop);
    return () => mediaQuery.removeEventListener('change', syncDesktop);
  }, []);

  // Mobil (iOS + Android): egen body-klasse så header og bunn-nav kan ligge synlige men inaktive bak varselpanelet
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onMobile = !isDesktop;
    if (isOpen && onMobile) {
      document.body.classList.add('talentfunn-notification-open');
    } else {
      document.body.classList.remove('talentfunn-notification-open');
    }
    return () => {
      document.body.classList.remove('talentfunn-notification-open');
    };
  }, [isOpen, isDesktop]);

  // Fetch notifications when opened or when accessToken changes (e.g. after re-login)
  useEffect(() => {
    devLog('🔔 NotificationCenter useEffect - isOpen:', isOpen);
    if (isOpen && accessToken) {
      devLog('🔔 Fetching notifications and scheduling markAllAsRead...');
      fetchNotifications();
      setTimeout(() => {
        devLog('🔔 Calling markAllAsRead after 100ms delay...');
        markAllAsRead();
      }, 100);
    }
  }, [isOpen, accessToken]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/notifications`);

      if (response.ok) {
        const data = await response.json();
        // Normalize: API may return "read", frontend uses "isRead"
        const list = (data.notifications || []).map((n: any) => ({
          ...n,
          isRead: n.isRead ?? n.read ?? false
        }));
        setNotifications(list);

        const hasUnreadVerification = list.some((n: Notification) =>
          !n.isRead &&
          n.type === 'system' &&
          (n.title.includes('verifisert') || n.title.includes('godkjent') || n.title.includes('avvist'))
        );
        
        // ✅ Refresh profile if employer was just verified/rejected
        if (hasUnreadVerification && onProfileRefresh) {
          devLog('🔄 Employer verification notification detected - refreshing profile...');
          onProfileRefresh();
        }
      }
    } catch (error) {
      devError('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/notifications/${notificationId}/read`, { method: 'PUT' });

      if (response.ok) {
        // Update local state
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
        );
        onNotificationRead(); // Refresh unread count
      }
    } catch (error) {
      devError('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    devLog('🔔 markAllAsRead() called - accessToken exists:', !!accessToken);
    try {
      devLog('🔔 Sending PUT request to /notifications/read-all...');
      const response = await fetchWithAuth(`${API_BASE}/notifications/read-all`, { method: 'PUT' });

      devLog('🔔 Response status:', response.status);

      if (response.status === 401) {
        // ✅ Token expired - silently ignore (token refresh happens in background)
        devLog('🔄 Token expired in markAllAsRead - will retry after refresh');
        return;
      }

      if (response.ok) {
        devLog('✅ markAllAsRead successful - updating local state and calling onNotificationRead');
        // Update local state
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        onNotificationRead(); // Refresh unread count
        // No toast - happens silently on open
      } else {
        devError('❌ Error marking all as read - status:', response.status);
        const errorData = await response.json().catch(() => ({}));
        devError('❌ Error response:', errorData);
      }
    } catch (error) {
      devError('❌ Exception in markAllAsRead:', error);
      // Don't show error toast either - fail silently
    }
  };

  const deleteNotification = async (notificationId: string) => {
    // Optimistic update - remove from UI immediately
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    onNotificationRead(); // Refresh unread count
    
    try {
      const response = await fetchWithAuth(`${API_BASE}/notifications/${notificationId}`, { method: 'DELETE' });

      if (!response.ok) {
        // Revert on error
        await fetchNotifications();
      }
    } catch (error) {
      devError('Error deleting notification:', error);
      // Revert on error
      await fetchNotifications();
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    const payload = notification.data || {};
    const destination = notification.destination || payload.destination || null;

    if (notification.type === 'gig_interest') {
      onClose();
      onNavigate('gig-interests', {
        gigId: payload.gigId,
        gigTitle: payload.gigTitle,
      });
      return;
    }

    if (destination === 'messages') {
      onClose();
      onNavigate('messages', {
        recipientId: payload.recipientId || payload.senderId,
      });
      return;
    }

    if (destination === 'saved-searches') {
      onClose();
      onNavigate('saved-searches', {
        savedSearchId: payload.savedSearchId,
      });
      return;
    }

    if (destination === 'profile') {
      onClose();
      onNavigate('profile', {
        userId: payload.userId || payload.viewerId,
      });
      return;
    }

    if (destination) {
      onClose();
      onNavigate(destination);
    }
  };

  const isNotificationNavigable = (notification: Notification) => {
    const payload = notification.data || {};
    return Boolean(notification.destination || payload.destination || notification.type === 'gig_interest');
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'admin_announcement':
      case 'system':
        return <Info className="w-5 h-5 text-blue-600" />;
      case 'profile_view':
        return <Eye className="w-5 h-5 text-purple-600" />;
      case 'premium_activated':
      case 'profile_approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'content_warning':
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      case 'gig_interest':
        return <Briefcase className="w-5 h-5 text-indigo-600" />;
      case 'admin_message':
        return <Bell className="w-5 h-5 text-purple-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Nå nettopp';
    if (diffMins < 60) return `${diffMins} min siden`;
    if (diffHours < 24) return `${diffHours} t siden`;
    if (diffDays < 7) return `${diffDays} d siden`;
    return new Date(dateString).toLocaleDateString('nb-NO');
  };

  if (!isOpen) return null;

  const desktopPanelStyle =
    isDesktopDropdown && anchorRect
      ? {
          top: `${Math.round(anchorRect.bottom + 8)}px`,
          right: `calc(100vw - ${Math.round(anchorRect.right)}px)`,
        }
      : undefined;

  return ReactDOM.createPortal(
    <div className={`fixed inset-0 z-[60] ${isDesktopDropdown ? 'xl:pointer-events-none' : ''}`}>
      <div
        className={
          isIosPwa
            ? 'notification-overlay-backdrop-ios-pwa'
            : 'absolute inset-0 talentfunn-modal-overlay backdrop-blur-md xl:hidden'
        }
        aria-hidden
      />
      <div className="relative h-full flex items-start justify-center xl:justify-end xl:items-start pt-[calc(4rem+env(safe-area-inset-top,0px))] xl:pt-[4.75rem] xl:pr-8">
        <div
          ref={modalRef}
          className={`bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 xl:mx-0 max-h-[80vh] xl:max-h-[72vh] xl:w-[25rem] xl:max-w-[25rem] flex flex-col border border-transparent xl:border-purple-100 xl:pointer-events-auto ${
            isDesktopDropdown ? 'xl:fixed xl:top-[4.75rem] xl:right-8' : ''
          }`}
          style={desktopPanelStyle}
        >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-purple-600" />
            <h2 className="font-semibold text-gray-900">Varsler</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader size="lg" className="text-purple-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Bell className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-gray-500">Ingen varsler ennå</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 transition-colors relative group ${
                    !notification.isRead ? 'bg-purple-50/50' : ''
                  }`}
                >
                  <div
                    onClick={() => handleNotificationClick(notification)}
                    className={`${isNotificationNavigable(notification) ? 'cursor-pointer' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0 pr-8">
                        <h3 className={`text-sm font-medium ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </h3>
                        <p className={`text-sm ${!notification.isRead ? 'text-gray-600' : 'text-gray-500'} mb-2`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400">
                          {getTimeAgo(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Delete button – always visible so it works on touch (no hover) */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                    className="absolute top-3 right-3 p-2 hover:bg-red-50 rounded-lg transition-all text-red-600"
                    aria-label="Slett varsel"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
